import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { useLanguage } from "@/i18n/LanguageContext";
import {
  adminCorrespondencePreview,
  adminCorrespondenceRecipients,
  adminSendCorrespondence,
  type ConferenceCorrespondenceExternalRecipient,
  type ConferenceCorrespondenceRecipient,
} from "../api";
import { PageHeader } from "../shells/PageHeader";
import { CorrespondenceBodyEditor } from "./CorrespondenceBodyEditor";

function typeLabel(
  t: ReturnType<typeof useLanguage>["t"],
  recipientType: string,
): string {
  if (recipientType === "registered_reviewer") return t.admin.corrTypeRegisteredReviewer;
  if (recipientType === "external_reviewer") return t.admin.corrTypeExternalReviewer;
  if (recipientType === "external") return t.admin.corrTypeExternal;
  return t.admin.corrTypeParticipant;
}

export function AdminCorrespondencePage() {
  const { t } = useLanguage();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<ConferenceCorrespondenceRecipient[]>([]);
  const [selected, setSelected] = useState<ConferenceCorrespondenceRecipient[]>([]);
  const [externalRecipients, setExternalRecipients] = useState<
    ConferenceCorrespondenceExternalRecipient[]
  >([]);
  const [externalEmail, setExternalEmail] = useState("");
  const [externalName, setExternalName] = useState("");
  const [addressee, setAddressee] = useState("");
  const [prayerHonorific, setPrayerHonorific] = useState("");
  const [location, setLocation] = useState("");
  const [greeting, setGreeting] = useState("");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [bodyHtml, setBodyHtml] = useState("<p></p>");
  const [closing, setClosing] = useState("");
  const [letterDate, setLetterDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [previewing, setPreviewing] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<{
    recipients?: string;
    addressee?: string;
    prayerHonorific?: string;
    location?: string;
    greeting?: string;
    title?: string;
    body?: string;
  }>({});
  const sendLock = useRef(false);
  const alertRef = useRef<HTMLDivElement>(null);

  const selectedIds = useMemo(() => new Set(selected.map((s) => s.user_id)), [selected]);
  const busy = previewing || sending;

  useEffect(() => {
    const handle = window.setTimeout(() => {
      adminCorrespondenceRecipients(query.trim() || undefined)
        .then(setResults)
        .catch(() => setResults([]));
    }, 250);
    return () => window.clearTimeout(handle);
  }, [query]);

  useEffect(() => {
    if ((success || error) && alertRef.current) {
      alertRef.current.focus();
    }
  }, [success, error]);

  const toggle = (row: ConferenceCorrespondenceRecipient) => {
    setSelected((prev) => {
      if (prev.some((p) => p.user_id === row.user_id)) {
        return prev.filter((p) => p.user_id !== row.user_id);
      }
      return [...prev, row];
    });
    setFieldErrors((fe) => ({ ...fe, recipients: undefined }));
  };

  const removeSelected = (userId: string) => {
    setSelected((prev) => prev.filter((p) => p.user_id !== userId));
  };

  const isPlainSectionText = (value: string): boolean => {
    for (const ch of value) {
      if (/\s/.test(ch) || ch === "." || ch === "," || ch === "،") continue;
      // Letters, numbers, and combining marks (e.g. Arabic tashkeel).
      if (/[\p{L}\p{N}\p{M}]/u.test(ch)) continue;
      return false;
    }
    return true;
  };

  const addExternalRecipient = () => {
    const email = externalEmail.trim().toLowerCase();
    const name = externalName.trim();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setFieldErrors((fe) => ({ ...fe, recipients: t.admin.msgInvalidEmail }));
      return;
    }
    setExternalRecipients((prev) => {
      if (prev.some((p) => p.email.toLowerCase() === email)) return prev;
      return [...prev, { email, name: name || null }];
    });
    setExternalEmail("");
    setExternalName("");
    setFieldErrors((fe) => ({ ...fe, recipients: undefined }));
  };

  const removeExternal = (email: string) => {
    setExternalRecipients((prev) => prev.filter((p) => p.email !== email));
  };

  const payload = () => ({
    recipient_user_ids: selected.map((s) => s.user_id),
    external_recipients: externalRecipients,
    title: title.trim(),
    body: body.trim(),
    body_html: bodyHtml.trim() || null,
    addressee: addressee.trim(),
    prayer_honorific: prayerHonorific.trim() || null,
    location: location.trim() || null,
    greeting: greeting.trim() || null,
    closing: closing.trim() || null,
    letter_date: letterDate || null,
    preview_recipient_user_id: selected[0]?.user_id ?? null,
  });

  const validateRequired = (): boolean => {
    const next: typeof fieldErrors = {};
    if (!selected.length && !externalRecipients.length) next.recipients = t.admin.corrNeedRecipients;
    if (!addressee.trim()) next.addressee = t.admin.corrNeedAddressee;
    else if (!isPlainSectionText(addressee.trim())) next.addressee = t.admin.corrPlainSectionInvalid;
    if (prayerHonorific.trim() && !isPlainSectionText(prayerHonorific.trim())) {
      next.prayerHonorific = t.admin.corrPlainSectionInvalid;
    }
    if (location.trim() && !isPlainSectionText(location.trim())) {
      next.location = t.admin.corrPlainSectionInvalid;
    }
    if (greeting.trim() && !isPlainSectionText(greeting.trim())) {
      next.greeting = t.admin.corrPlainSectionInvalid;
    }
    if (!title.trim()) next.title = t.admin.corrNeedTitle;
    if (!body.trim()) next.body = t.admin.corrNeedBody;
    setFieldErrors(next);
    if (Object.keys(next).length) {
      setError(t.admin.corrFixRequired);
      setSuccess(null);
      return false;
    }
    return true;
  };

  const resetFormAfterSend = () => {
    setSelected([]);
    setExternalRecipients([]);
    setExternalEmail("");
    setExternalName("");
    setAddressee("");
    setPrayerHonorific("");
    setLocation("");
    setGreeting("");
    setTitle("");
    setBody("");
    setBodyHtml("<p></p>");
    setClosing("");
    setLetterDate(new Date().toISOString().slice(0, 10));
    setFieldErrors({});
  };

  const onPreview = async () => {
    if (!validateRequired()) return;
    setPreviewing(true);
    setError(null);
    setSuccess(null);
    try {
      const blob = await adminCorrespondencePreview(payload());
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
      setSuccess(t.admin.corrPreviewReady);
    } catch (e) {
      setError(e instanceof Error ? e.message : t.common.error);
    } finally {
      setPreviewing(false);
    }
  };

  const onSend = async () => {
    if (sendLock.current || sending) return;
    if (!validateRequired()) return;
    sendLock.current = true;
    setSending(true);
    setError(null);
    setSuccess(null);
    try {
      const result = await adminSendCorrespondence(payload());
      if (result.failed > 0 && result.sent === 0) {
        setError(
          t.admin.corrSendAllFailed
            .replace("{failed}", String(result.failed))
            .replace("{total}", String(result.sent + result.failed)),
        );
      } else if (result.failed > 0) {
        setSuccess(null);
        setError(
          t.admin.corrSendPartial
            .replace("{sent}", String(result.sent))
            .replace("{failed}", String(result.failed)),
        );
      } else {
        const msg =
          result.sent === 1 && result.items[0]
            ? t.admin.corrSendSuccessOne.replace("{name}", result.items[0].recipient_name)
            : t.admin.corrSendSuccessMany.replace("{count}", String(result.sent));
        resetFormAfterSend();
        setSuccess(msg);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t.common.error);
    } finally {
      setSending(false);
      sendLock.current = false;
    }
  };

  return (
    <div className="dn-corr-page">
      <PageHeader
        title={t.admin.correspondence}
        lead={t.admin.correspondenceLead}
        backTo="/admin"
        actions={
          <Link to="/admin/correspondence/history" className="dn-btn dn-btn-ghost">
            {t.admin.corrHistory}
          </Link>
        }
      />

      {(error || success) && (
        <div
          ref={alertRef}
          tabIndex={-1}
          role="status"
          aria-live="polite"
          className={`dn-alert ${error ? "dn-alert-error" : "dn-alert-success"} dn-corr-toast`}
        >
          <div className="dn-corr-toast-body">
            <strong>{error ? t.admin.corrNoticeError : t.admin.corrNoticeSuccess}</strong>
            <p>{error || success}</p>
            <Link to="/admin/correspondence/history" className="dn-corr-toast-link">
              {t.admin.corrViewHistory}
            </Link>
          </div>
          <button
            type="button"
            className="dn-btn dn-btn-ghost"
            onClick={() => {
              setError(null);
              setSuccess(null);
            }}
          >
            {t.common.close}
          </button>
        </div>
      )}

      <div className="dn-corr-layout">
        <section className="dn-card dn-corr-panel dn-corr-recipients" aria-labelledby="dn-corr-recipients-heading">
          <div className="dn-corr-panel-head">
            <h2 id="dn-corr-recipients-heading">{t.admin.corrRecipientsHeading}</h2>
            <span className="dn-muted">
              {selected.length + externalRecipients.length
                ? t.admin.corrSelectedCount.replace(
                    "{count}",
                    String(selected.length + externalRecipients.length),
                  )
                : t.admin.corrSelectedNone}
            </span>
          </div>

          <label className="dn-corr-field">
            <span>
              {t.admin.corrSearchRecipients}
              <abbr className="dn-corr-required" title={t.admin.corrRequired}>
                *
              </abbr>
            </span>
            <input
              type="search"
              value={query}
              onChange={(ev) => setQuery(ev.target.value)}
              placeholder={t.admin.corrSearchPlaceholder}
              disabled={busy}
              aria-invalid={Boolean(fieldErrors.recipients)}
            />
            {fieldErrors.recipients && <span className="dn-corr-field-error">{fieldErrors.recipients}</span>}
          </label>

          <div className="dn-corr-external-block">
            <p className="dn-muted">{t.admin.corrExternalHelp}</p>
            <div className="dn-corr-external-row">
              <label className="dn-corr-field">
                <span>{t.admin.corrExternalEmail}</span>
                <input
                  type="email"
                  value={externalEmail}
                  onChange={(ev) => setExternalEmail(ev.target.value)}
                  placeholder="name@example.com"
                  disabled={busy}
                  onKeyDown={(ev) => {
                    if (ev.key === "Enter") {
                      ev.preventDefault();
                      addExternalRecipient();
                    }
                  }}
                />
              </label>
              <label className="dn-corr-field">
                <span>{t.admin.corrExternalName}</span>
                <input
                  type="text"
                  value={externalName}
                  onChange={(ev) => setExternalName(ev.target.value)}
                  placeholder={t.admin.corrExternalNamePlaceholder}
                  disabled={busy}
                  onKeyDown={(ev) => {
                    if (ev.key === "Enter") {
                      ev.preventDefault();
                      addExternalRecipient();
                    }
                  }}
                />
              </label>
              <button
                type="button"
                className="dn-btn dn-btn-ghost"
                disabled={busy || !externalEmail.trim()}
                onClick={addExternalRecipient}
              >
                {t.admin.corrExternalAdd}
              </button>
            </div>
          </div>

          {externalRecipients.length > 0 && (
            <ul className="dn-corr-chips" aria-label={t.admin.corrExternalHeading}>
              {externalRecipients.map((s) => (
                <li key={s.email}>
                  <button
                    type="button"
                    className="dn-corr-chip is-selected"
                    disabled={busy}
                    onClick={() => removeExternal(s.email)}
                    title={t.admin.corrRemoveRecipient}
                  >
                    <span className="dn-corr-chip-name">{s.name || s.email}</span>
                    <span className="dn-badge">{typeLabel(t, "external")}</span>
                    <span aria-hidden="true">×</span>
                  </button>
                </li>
              ))}
            </ul>
          )}

          {selected.length > 0 && (
            <ul className="dn-corr-chips" aria-label={t.admin.corrSelectedCount.replace("{count}", String(selected.length))}>
              {selected.map((s) => (
                <li key={s.user_id}>
                  <button
                    type="button"
                    className="dn-corr-chip is-selected"
                    disabled={busy}
                    onClick={() => removeSelected(s.user_id)}
                    title={t.admin.corrRemoveRecipient}
                  >
                    <span className="dn-corr-chip-name">{s.name}</span>
                    <span className="dn-badge">{typeLabel(t, s.recipient_type)}</span>
                    <span aria-hidden="true">×</span>
                  </button>
                </li>
              ))}
            </ul>
          )}

          <div className="dn-table-wrap dn-corr-recipient-table">
            <table className="dn-table dn-table-responsive">
              <thead>
                <tr>
                  <th scope="col" className="dn-corr-check-col">
                    <span className="dn-sr-only">{t.admin.corrSelect}</span>
                  </th>
                  <th scope="col">{t.admin.corrRecipient}</th>
                  <th scope="col">Email</th>
                  <th scope="col">{t.admin.corrType}</th>
                </tr>
              </thead>
              <tbody>
                {results.map((row) => {
                  const checked = selectedIds.has(row.user_id);
                  return (
                    <tr
                      key={row.user_id}
                      className={checked ? "dn-corr-row-selected" : undefined}
                      onClick={() => !busy && toggle(row)}
                    >
                      <td className="dn-corr-check-col" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={busy}
                          onChange={() => toggle(row)}
                          aria-label={row.name}
                        />
                      </td>
                      <td>
                        <strong>{row.name}</strong>
                      </td>
                      <td className="dn-corr-email">{row.email}</td>
                      <td>
                        <span className="dn-badge">{typeLabel(t, row.recipient_type)}</span>
                      </td>
                    </tr>
                  );
                })}
                {!results.length && (
                  <tr>
                    <td colSpan={4} className="dn-muted">
                      {t.admin.corrNoRecipients}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        <section className="dn-card dn-corr-panel dn-corr-compose" aria-labelledby="dn-corr-compose-heading">
          <div className="dn-corr-panel-head">
            <h2 id="dn-corr-compose-heading">{t.admin.corrComposeHeading}</h2>
            <p className="dn-muted dn-corr-compose-hint">{t.admin.corrComposeHint}</p>
          </div>

          <label className="dn-corr-field">
            <span>
              {t.admin.corrAddressee}
              <abbr className="dn-corr-required" title={t.admin.corrRequired}>
                *
              </abbr>
            </span>
            <textarea
              rows={2}
              value={addressee}
              onChange={(ev) => {
                setAddressee(ev.target.value);
                setFieldErrors((fe) => ({ ...fe, addressee: undefined }));
              }}
              placeholder={t.admin.corrAddresseePlaceholder}
              disabled={busy}
              dir="auto"
              aria-invalid={Boolean(fieldErrors.addressee)}
              required
            />
            {fieldErrors.addressee && <span className="dn-corr-field-error">{fieldErrors.addressee}</span>}
          </label>

          <label className="dn-corr-field">
            <span>{t.admin.corrPrayerHonorific}</span>
            <input
              value={prayerHonorific}
              onChange={(ev) => {
                setPrayerHonorific(ev.target.value);
                setFieldErrors((fe) => ({ ...fe, prayerHonorific: undefined }));
              }}
              placeholder={t.admin.corrPrayerHonorificPlaceholder}
              disabled={busy}
              dir="auto"
              aria-invalid={Boolean(fieldErrors.prayerHonorific)}
            />
            {fieldErrors.prayerHonorific && (
              <span className="dn-corr-field-error">{fieldErrors.prayerHonorific}</span>
            )}
          </label>

          <label className="dn-corr-field">
            <span>{t.admin.corrLocation}</span>
            <input
              value={location}
              onChange={(ev) => {
                setLocation(ev.target.value);
                setFieldErrors((fe) => ({ ...fe, location: undefined }));
              }}
              placeholder={t.admin.corrLocationPlaceholder}
              disabled={busy}
              dir="auto"
              aria-invalid={Boolean(fieldErrors.location)}
            />
            {fieldErrors.location && <span className="dn-corr-field-error">{fieldErrors.location}</span>}
          </label>

          <label className="dn-corr-field">
            <span>{t.admin.corrGreeting}</span>
            <input
              value={greeting}
              onChange={(ev) => {
                setGreeting(ev.target.value);
                setFieldErrors((fe) => ({ ...fe, greeting: undefined }));
              }}
              placeholder={t.admin.corrGreetingPlaceholder}
              disabled={busy}
              dir="auto"
              aria-invalid={Boolean(fieldErrors.greeting)}
            />
            {fieldErrors.greeting && <span className="dn-corr-field-error">{fieldErrors.greeting}</span>}
          </label>

          <label className="dn-corr-field">
            <span>
              {t.admin.corrTitle}
              <abbr className="dn-corr-required" title={t.admin.corrRequired}>
                *
              </abbr>
            </span>
            <input
              value={title}
              onChange={(ev) => {
                setTitle(ev.target.value);
                setFieldErrors((fe) => ({ ...fe, title: undefined }));
              }}
              placeholder={t.admin.corrTitlePlaceholder}
              disabled={busy}
              dir="auto"
              aria-invalid={Boolean(fieldErrors.title)}
              required
            />
            {fieldErrors.title && <span className="dn-corr-field-error">{fieldErrors.title}</span>}
          </label>

          <div className="dn-corr-field dn-corr-field-body">
            <span>
              {t.admin.corrBody}
              <abbr className="dn-corr-required" title={t.admin.corrRequired}>
                *
              </abbr>
            </span>
            <CorrespondenceBodyEditor
              valueHtml={bodyHtml}
              disabled={busy}
              invalid={Boolean(fieldErrors.body)}
              labels={{
                bold: t.admin.corrRteBold,
                italic: t.admin.corrRteItalic,
                underline: t.admin.corrRteUnderline,
                alignLeft: t.admin.corrRteAlignLeft,
                alignCenter: t.admin.corrRteAlignCenter,
                alignRight: t.admin.corrRteAlignRight,
                alignJustify: t.admin.corrRteAlignJustify,
                bulletList: t.admin.corrRteBulletList,
                orderedList: t.admin.corrRteOrderedList,
                undo: t.admin.corrRteUndo,
                redo: t.admin.corrRteRedo,
                fontSize: t.admin.corrRteFontSize,
              }}
              onChange={(html, plain) => {
                setBodyHtml(html);
                setBody(plain);
                setFieldErrors((fe) => ({ ...fe, body: undefined }));
              }}
            />
            {fieldErrors.body && <span className="dn-corr-field-error">{fieldErrors.body}</span>}
          </div>

          <label className="dn-corr-field">
            <span>{t.admin.corrClosing}</span>
            <textarea
              rows={2}
              value={closing}
              onChange={(ev) => setClosing(ev.target.value)}
              placeholder={t.admin.corrClosingPlaceholder}
              disabled={busy}
              dir="auto"
            />
          </label>

          <div className="dn-corr-meta">
            <label className="dn-corr-field">
              <span>{t.admin.corrDate}</span>
              <input
                type="date"
                value={letterDate}
                onChange={(ev) => setLetterDate(ev.target.value)}
                disabled={busy}
              />
            </label>
          </div>

          <div className="dn-corr-actions">
            <button type="button" className="dn-btn dn-btn-ghost" disabled={busy} onClick={onPreview}>
              {previewing ? t.admin.corrPreviewing : t.admin.corrPreviewPdf}
            </button>
            <button
              type="button"
              className="dn-btn dn-btn-gold dn-corr-send"
              disabled={busy}
              aria-busy={sending}
              onClick={onSend}
            >
              {sending ? (
                <>
                  <span className="dn-corr-spinner" aria-hidden="true" />
                  {t.admin.corrSending}
                </>
              ) : (
                t.admin.corrSend
              )}
            </button>
            <Link to="/admin/correspondence/history" className="dn-btn dn-btn-ghost dn-corr-history-link">
              {t.admin.corrHistory}
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
}
